/** Centralized matter type -> display label / color / icon mapping. */

import type { MatterType, MatterStage } from "../types";

// ---------------------------------------------------------------------------
// Matter type -> Chinese label
// ---------------------------------------------------------------------------

export const MATTER_TYPE_LABELS: Record<MatterType, string> = {
  litigation: "诉讼/仲裁",
  merger_acquisition: "并购",
  capital_markets: "资本市场",
  private_equity: "私募股权/投资基金",
  banking_finance: "银行与金融",
  corporate: "公司业务",
  general_advisory: "常年法律顾问",
  compliance: "合规",
  ip: "知识产权",
  employment: "劳动",
  real_estate: "房地产/建设工程",
  restructuring: "破产重整",
  other: "其他",
};

// ---------------------------------------------------------------------------
// Matter type -> CSS color value (dot / badge foreground)
// ---------------------------------------------------------------------------

export const MATTER_TYPE_COLORS: Record<MatterType, string> = {
  litigation: "var(--jerry-type-litigation)",
  merger_acquisition: "var(--jerry-type-ma)",
  capital_markets: "var(--jerry-type-ipo)",
  private_equity: "var(--jerry-type-ma)",
  banking_finance: "var(--jerry-type-finance)",
  corporate: "var(--jerry-info)",
  general_advisory: "var(--jerry-type-advisory)",
  compliance: "var(--jerry-type-compliance)",
  ip: "var(--jerry-type-ipo)",
  employment: "var(--jerry-type-employment)",
  real_estate: "var(--jerry-type-real-estate)",
  restructuring: "var(--jerry-warning)",
  other: "var(--jerry-text-secondary)",
};

// ---------------------------------------------------------------------------
// Matter type -> badge background (tinted) + foreground color
// ---------------------------------------------------------------------------

export const MATTER_TYPE_BADGE_STYLES: Record<
  MatterType,
  { background: string; color: string }
> = {
  litigation: { background: "var(--jerry-danger-bg)", color: "var(--jerry-danger)" },
  merger_acquisition: { background: "var(--jerry-info-bg)", color: "var(--jerry-info)" },
  capital_markets: { background: "var(--jerry-purple-bg)", color: "var(--jerry-purple)" },
  private_equity: { background: "var(--jerry-info-bg)", color: "var(--jerry-info)" },
  banking_finance: { background: "var(--jerry-type-finance-bg)", color: "var(--jerry-type-finance)" },
  corporate: { background: "var(--jerry-info-bg)", color: "var(--jerry-info)" },
  general_advisory: { background: "var(--jerry-success-bg)", color: "var(--jerry-success)" },
  compliance: { background: "var(--jerry-warning-bg)", color: "var(--jerry-warning)" },
  ip: { background: "var(--jerry-purple-bg)", color: "var(--jerry-purple)" },
  employment: { background: "var(--jerry-type-employment-bg)", color: "var(--jerry-type-employment)" },
  real_estate: { background: "var(--jerry-type-real-estate-bg)", color: "var(--jerry-type-real-estate)" },
  restructuring: { background: "var(--jerry-warning-bg)", color: "var(--jerry-warning)" },
  other: { background: "var(--jerry-type-other-bg)", color: "var(--jerry-text-secondary)" },
};

// ---------------------------------------------------------------------------
// Matter type -> dot class key (for CSS module class lookup)
// ---------------------------------------------------------------------------

export const MATTER_TYPE_DOT_CLASS: Record<MatterType, string> = {
  litigation: "litigation",
  merger_acquisition: "merger_acquisition",
  capital_markets: "capital_markets",
  private_equity: "private_equity",
  banking_finance: "banking_finance",
  corporate: "corporate",
  general_advisory: "general_advisory",
  compliance: "compliance",
  ip: "ipc",
  employment: "employment",
  real_estate: "real_estate",
  restructuring: "restructuring",
  other: "other",
};

// ---------------------------------------------------------------------------
// Matter stage -> Chinese label
// ---------------------------------------------------------------------------

export const MATTER_STAGE_LABELS: Record<MatterStage, string> = {
  // Generic stages
  intake: "接洽",
  evaluation: "评估",
  active: "承办中",
  closing: "结案",
  archived: "归档",
  initiated: "启动",
  in_progress: "进行中",
  completed: "完成",
  // Litigation stages
  case_evaluation: "事项评估",
  evidence_collection: "证据收集",
  filing: "立案",
  defense: "答辩",
  evidence_exchange: "证据交换",
  pre_trial: "庭前准备",
  trial: "庭审",
  judgment: "判决",
  enforcement: "执行",
  // M&A stages
  preliminary: "前期接洽",
  nda_signed: "NDA签署",
  ts_negotiation: "TS谈判",
  ts_signed: "TS签署",
  due_diligence: "尽职调查",
  spa_drafting: "SPA起草",
  spa_signed: "SPA签署",
  pre_closing: "交割准备",
  post_closing: "交割后整合",
};
